import { memo, useMemo } from 'react';
import css from './index.module.scss';
import { IconNode, Typography } from '@components/base';
import Images from '@assets/images';
import { sanitizeValue } from '@helpers/utils';

interface OrderDetailContainerProps {
  onEditClick?: () => void;
  modeOfTransportation: string;
  incoTerm: string;
  countryOfOrigin: string;
  countryOfFinalDestination: string;
  portOfLoading: string;
  portOfDischarge: string;
  placeOfDelivery: string;
  paymentTerm: Array<string>;
  deliveryTerm: string;
  qualityTesting: string;
  previewForm?: boolean;
  palletization: boolean;
  isDomestic: boolean;
}

const ORDER_DETAIL_FIELDS = [
  { key: 'modeOfTransportation', label: 'Mode of Transportation' },
  { key: 'incoTerm', label: 'Inco Terms' },
  {
    key: 'palletization',
    label: 'Item to be palletized',
    value: (p: OrderDetailContainerProps) => (p.palletization ? 'Yes' : 'No')
  },

  { key: 'countryOfOrigin', label: 'Country of Origin' },
  { key: 'countryOfFinalDestination', label: 'Country of Final Destination' },
  {
    key: 'qualityTesting',
    label: 'Quality Testing',
    value: (p: OrderDetailContainerProps) => sanitizeValue(p.qualityTesting)
  },

  {
    key: 'portOfLoading',
    label: 'Port of Loading',
    visible: (p: OrderDetailContainerProps) => !p.isDomestic
  },

  {
    key: 'portOfDischarge',
    label: 'Port of Discharge',
    visible: (p: OrderDetailContainerProps) => !p.isDomestic
  },

  { key: 'placeOfDelivery', label: 'Place of Delivery' },
  { key: 'deliveryTerm', label: 'Delivery Terms' },

  {
    key: 'paymentTerm',
    label: 'Payment Terms',
    value: (p: OrderDetailContainerProps) => p.paymentTerm ?? []
  }
];

const OrderDetailContainer = (props: OrderDetailContainerProps) => {
  const { onEditClick, previewForm = true } = props;

  const visibleFields = useMemo(
    () => ORDER_DETAIL_FIELDS.filter(({ visible }) => (visible ? visible(props) : true)),
    [props]
  );

  return (
    <div className={css.outlineWrapper}>
      <div className={css.headerWrapper}>
        <Typography variant="pdoc">Order Details</Typography>
        {previewForm ? (
          <IconNode
            src={Images.editRed}
            alt="edit icon"
            className={css.editButton}
            component="button"
            onClick={onEditClick}
          />
        ) : (
          <></>
        )}
      </div>
      <div className={css.gridContainer}>
        {visibleFields.map(({ key, label, value }) => {
          const raw = typeof value === 'function' ? value(props) : (props as any)[key];
          const list = Array.isArray(raw) ? raw : [raw];

          return (
            <div key={key} className={css.columnWrapper}>
              <Typography variant="ptext">{label}</Typography>

              {list.map((v, i) => (
                <Typography variant="pdoc" key={i}>
                  {sanitizeValue(v)}
                </Typography>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default memo(OrderDetailContainer);
